import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CollectionListItem } from '../types/collection';
import { TrashIcon } from './CustomIcons';
import { PinIcon } from './PinIcon';
import ConfirmationModal from './ConfirmationModal';
import { useAuth } from '../contexts/FirebaseAuthContext';
import '../styles/CollectionCard.css';

interface CollectionCardProps {
  item: CollectionListItem;
  onDelete?: (id: string) => void;
  showDragHandle?: boolean;
  isPinned?: boolean;
  onPin?: (e: React.MouseEvent) => void;
  canPin?: boolean;
}

export const CollectionCard: React.FC<CollectionCardProps> = ({
  item,
  onDelete,
  showDragHandle = false,
  isPinned = false,
  onPin,
  canPin = false
}) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { collection, completedCount, lastPracticed, completionPercentage } = item;
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  
  const handleClick = () => {
    navigate(`/collections/${collection.id}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (onDelete) {
      onDelete(collection.id);
    }
    setShowDeleteModal(false);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handlePinClick = (e: React.MouseEvent) => {
    console.log('[PIN DEBUG] CollectionCard handlePinClick called');
    e.stopPropagation();
    e.preventDefault();
    if (onPin) {
      console.log('[PIN DEBUG] Calling onPin handler');
      onPin(e);
    } else {
      console.log('[PIN DEBUG] onPin handler is undefined!');
    }
  };

  const getTypeBadge = () => {
    const typeStyles = {
      company: { bg: '#E3F2FD', color: '#1976D2', label: 'By Company' },
      custom: { bg: '#E8F5E9', color: '#388E3C', label: 'Custom' },
      topic: { bg: '#FFF3E0', color: '#F57C00', label: 'By Author' },
      curated: { bg: '#F3E5F5', color: '#7B1FA2', label: 'By Pattern' }
    };
    
    const style = typeStyles[collection.type];
    return (
      <span 
        className="collection-type-badge" 
        style={{ backgroundColor: style.bg, color: style.color }}
      >
        {style.label}
      </span>
    );
  };

  const getDifficultyDots = () => {
    const { easy, medium, hard } = collection.stats.difficulty;
    return (
      <div className="difficulty-dots">
        {easy > 0 && <span className="dot easy">● Easy: {easy}</span>}
        {medium > 0 && <span className="dot medium">● Medium: {medium}</span>}
        {hard > 0 && <span className="dot hard">● Hard: {hard}</span>}
      </div>
    );
  };

  return (
    <div className="collection-card" onClick={handleClick}>
      {showDragHandle && (
        <div className="drag-handle" title="Drag to reorder">
          ⋮⋮
        </div>
      )}
      <div className="collection-header">
        <h3 className="collection-title">{collection.name}</h3>
        <div className="collection-header-right">
          {canPin && onPin && (
            <PinIcon
              isPinned={isPinned}
              onClick={handlePinClick}
              size={18}
            />
          )}
          {getTypeBadge()}
        </div>
      </div>
      
      <p className="collection-description">{collection.description}</p>
      
      <div className="collection-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <span className="progress-text">
          {completedCount}/{collection.stats.totalProblems} completed
        </span>
      </div>
      
      {getDifficultyDots()}
      
      <div className="collection-footer">
        <span className="last-practiced">
          {lastPracticed 
            ? `Last practiced: ${getRelativeTime(lastPracticed)}`
            : 'Not started'}
        </span>
        {onDelete && userProfile && collection.ownerId === userProfile.uid && (
          <button 
            className="delete-btn"
            onClick={handleDelete}
            title="Delete collection"
          >
            <TrashIcon size={16} />
          </button>
        )}
      </div>
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Collection"
        message={`Are you sure you want to delete "${collection.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return `${Math.floor(diffInDays / 30)} months ago`;
}